import { useEffect, useRef, useState, type FC } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import { studentApi } from './api';
import { CourseStudentCabinetGrid } from './components/CourseStudentCabinetGrid';
import { StudentCourseWorkspace } from './components/StudentCourseWorkspace';
import { StudentLiteratureOverlay } from './components/StudentLiteratureOverlay';
import { StudentScientificProjectsOverlay } from './components/StudentScientificProjectsOverlay';
import { StudentOpportunitiesOverlay } from './components/StudentOpportunitiesOverlay';
import { StudentOfficialSourcesOverlay } from './components/StudentOfficialSourcesOverlay';
import { StudentAIModal } from './components/StudentAIModal';
import { StudentSubmissionModal } from './components/StudentSubmissionModal';
import { StudentRequestModal } from './components/StudentRequestModal';
import { StudentDocumentModal } from './components/StudentDocumentModal';
import { StudentChatModal } from './components/StudentChatModal';
import { CreateRequestEntry } from '@/features/request-gateway';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { CourseMeetings } from '@/features/course/CourseMeetings';
import type {
  StudentAssignment,
  StudentAttendanceOverview,
  StudentDiscipline,
  StudentDocumentTemplate,
  StudentGradeOverview,
  StudentGroupChatMessage,
  StudentKPI,
  StudentLessonWeek,
  StudentLiteratureItem,
  StudentMeeting,
  StudentOpportunity,
  StudentPortfolioItem,
  StudentProfile,
  StudentRequest,
  StudentSavedLiterature,
  StudentScientificProject,
} from './types';

export const StudentPage: FC = () => {
  const { notify } = useToast();
  const navigate = useNavigate();
  const backToCabinet = () => navigate(routes.courseStudent);

  const [isAIOpen, setIsAIOpen] = useState(false);
  const [isCreateRequestOpen, setIsCreateRequestOpen] = useState(false);
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  const [activeAssignment, setActiveAssignment] = useState<StudentAssignment | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<StudentDocumentTemplate | null>(null);

  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [kpi, setKpi] = useState<StudentKPI | null>(null);
  const [disciplines, setDisciplines] = useState<StudentDiscipline[]>([]);
  const [selectedDisciplineCode, setSelectedDisciplineCode] = useState<string>('');
  const [weeks, setWeeks] = useState<StudentLessonWeek[]>([]);
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [grades, setGrades] = useState<StudentGradeOverview>({
    attestation_1: 0,
    attestation_2: 0,
    exam: 0,
    total_rating: 0,
    letter_grade: 'FX',
    breakdown: [],
  });
  const [attendance, setAttendance] = useState<StudentAttendanceOverview>({
    present_count: 0,
    absent_count: 0,
    valid_excused_count: 0,
    total_hours: 0,
    percent: 0,
    records: [],
  });
  const [templates, setTemplates] = useState<StudentDocumentTemplate[]>([]);
  const [portfolio, setPortfolio] = useState<StudentPortfolioItem[]>([]);
  const [requests, setRequests] = useState<StudentRequest[]>([]);
  const [savedLiterature, setSavedLiterature] = useState<StudentSavedLiterature[]>([]);
  const [chatMessages, setChatMessages] = useState<StudentGroupChatMessage[]>([]);
  const [meetings, setMeetings] = useState<StudentMeeting[]>([]);
  const [scientificProjects, setScientificProjects] = useState<StudentScientificProject[]>([]);
  const [opportunities, setOpportunities] = useState<StudentOpportunity[]>([]);

  useEffect(() => {
    const generation = ++contextGeneration.current;
    const loadInitialData = async () => {
      try {
        const sum = await studentApi.getSummary();
        if (generation !== contextGeneration.current) return;
        setProfile(sum.profile);
        setKpi(sum.kpi);
        setDisciplines(sum.disciplines || []);
        setScientificProjects(sum.scientific_projects || []);
        setRequests(sum.recent_requests || []);
        setOpportunities(sum.opportunities || []);

        const discCode = sum.disciplines?.[0]?.code || '';
        if (!discCode) return;
        setSelectedDisciplineCode(discCode);

        const groupCode = sum.disciplines?.[0]?.group_code || '';

        const [wList, asList, grList, attList, tmplList, portList, litList, chatList, meetList] =
          await Promise.all([
            studentApi.getWeeks(discCode),
            studentApi.getAssignments(discCode),
            studentApi.getGrades(discCode),
            studentApi.getAttendance(discCode),
            studentApi.getDocuments(),
            studentApi.getPortfolio(),
            studentApi.getSavedLiterature(),
            studentApi.getGroupChatMessages(groupCode, discCode),
            studentApi.getMeetings(),
          ]);

        if (generation !== contextGeneration.current) return;
        setWeeks(wList);
        setAssignments(asList);
        setGrades(grList);
        setAttendance(attList);
        setTemplates(tmplList);
        setPortfolio(portList);
        setSavedLiterature(litList);
        setChatMessages(chatList);
        setMeetings(meetList);
      } catch {
        notify('Не удалось загрузить данные кабинета обучающегося');
      }
    };

    void loadInitialData();
    return () => { contextGeneration.current++; };
  }, []);

  const contextGeneration = useRef(0);
  const handleSelectDiscipline = async (code: string) => {
    const generation = ++contextGeneration.current;
    setSelectedDisciplineCode(code); setWeeks([]); setAssignments([]); setChatMessages([]);
    setGrades({ attestation_1: 0, attestation_2: 0, exam: 0, total_rating: 0, letter_grade: '—', breakdown: [], items: [], control_points: [], current_score: 0, attendance_rate: 0 });
    setAttendance({ present_count: 0, absent_count: 0, valid_excused_count: 0, total_hours: 0, percent: 0, records: [], logs: [], attendance_rate: 0 }); setIsSubmissionOpen(false); setActiveAssignment(null);
    try {
      const group = disciplines.find(d => d.code === code)?.group_code;
      const [wList, asList, grList, attList, chat] = await Promise.all([
        studentApi.getWeeks(code), studentApi.getAssignments(code), studentApi.getGrades(code), studentApi.getAttendance(code),
        group ? studentApi.getGroupChatMessages(group, code) : Promise.resolve([]),
      ]);
      if (generation !== contextGeneration.current) return;
      setWeeks(wList); setAssignments(asList); setGrades(grList); setAttendance(attList); setChatMessages(chat);
    } catch (e) { if (generation === contextGeneration.current) notify(e instanceof Error ? e.message : 'Не удалось загрузить дисциплину'); }
  };

  const handleOpenUploadModal = (assignment: StudentAssignment) => {
    setActiveAssignment(assignment);
    setIsSubmissionOpen(true);
  };

  const handleSubmitAssignment = async (
    assignmentId: string,
    payload: { attachment_id?: string; client_request_id?: string; feedback_note?: string },
  ) => {
    const generation = contextGeneration.current;
    await studentApi.submitAssignmentVersion(assignmentId, payload);
    if (generation !== contextGeneration.current) return;
    notify('Задание успешно отправлено на проверку');
    setIsSubmissionOpen(false);
    const [asList, grList] = await Promise.all([
      studentApi.getAssignments(selectedDisciplineCode),
      studentApi.getGrades(selectedDisciplineCode),
    ]);
    if (generation !== contextGeneration.current) return;
    setAssignments(asList);
    setGrades(grList);
  };

  const handleCreateRequest = async (payload: {
    discipline_code: string;
    category: string;
    title: string;
    message: string;
  }) => {
    await studentApi.createRequest(payload);
    notify('Обращение успешно направлено преподавателю');
    setIsRequestModalOpen(false);
    const reqList = await studentApi.getRequests();
    setRequests(reqList);
  };

  const handleTogglePortfolio = async (title: string, workType: string, filename?: string) => {
    await studentApi.togglePortfolio({
      submission_id: assignments.find(a => a.title === title)?.my_submission?.id,
      title,
      work_type: workType,
      discipline_code: selectedDisciplineCode,
      filename: filename || 'document.pdf',
    });
    notify('Работа добавлена в портфолио обучающегося');
    const portList = await studentApi.getPortfolio();
    setPortfolio(portList);
  };

  const handleSearchLiterature = async (req: {
    topic: string;
    discipline: string;
    source_type: string;
    period: string;
    language: string;
    databases: string[];
    full_text_only: boolean;
    limit: number;
    extra_criteria?: string;
  }): Promise<StudentLiteratureItem[]> => {
    return await studentApi.searchLiterature(req);
  };

  const handleSaveLiterature = async (item: StudentLiteratureItem) => {
    await studentApi.saveLiterature(item);
    notify('Источник сохранен в избранное');
    const litList = await studentApi.getSavedLiterature();
    setSavedLiterature(litList);
  };

  const handleDeleteSavedLiterature = async (id: string) => {
    await studentApi.deleteSavedLiterature(id);
    notify('Источник удален из сохраненных');
    const litList = await studentApi.getSavedLiterature();
    setSavedLiterature(litList);
  };

  const handleToggleOpportunity = async (id: string, currentTracked: boolean) => {
    await studentApi.updateOpportunity(id, { is_tracked: !currentTracked });
    notify(!currentTracked ? 'Добавлено в отслеживаемые' : 'Удалено из отслеживаемых');
    const oppList = await studentApi.getOpportunities();
    setOpportunities(oppList);
  };

  const handleSendChatMessage = async (text: string) => {
    const groupCode = disciplines.find(d => d.code === selectedDisciplineCode)?.group_code;
    if (!groupCode) return;
    await studentApi.sendGroupChatMessage({ discipline_code: selectedDisciplineCode, group_code: groupCode, message: text });
    const chatList = await studentApi.getGroupChatMessages(groupCode, selectedDisciplineCode);
    setChatMessages(chatList);
  };

  const handleAskAI = async (prompt: string) => {
    return await studentApi.getAISuggestion({ prompt, discipline_code: selectedDisciplineCode });
  };

  const cabinetHome = (
    <CabinetWorkspaceFrame
      links={{
        notebook: routes.courseStudentModule('literature'),
        calendar: routes.courseStudentModule('meetings'),
        meetings: routes.courseStudentModule('meetings'),
        connections: routes.courseStudentModule('connections'),
        alliances: routes.courseStudentModule('connections'),
        qa: routes.portalSection('instructions'),
        support: routes.portalSection('contacts'),
      }}
    >
      <CourseStudentCabinetGrid
        profile={profile}
        kpi={kpi}
        onOpenWorkspace={() => navigate(routes.courseStudentModule('course-workspace'))}
        onOpenLiterature={() => navigate(routes.courseStudentModule('literature'))}
        onOpenConnections={() => navigate(routes.courseStudentModule('connections'))}
        onOpenTools={() => navigate(routes.courseStudentModule('tools'))}
        onOpenMeetings={() => navigate(routes.courseStudentModule('meetings'))}
        onOpenScientificProjects={() => navigate(routes.courseStudentModule('scientific-projects'))}
        onOpenOpportunities={() => navigate(routes.courseStudentModule('opportunities'))}
        onOpenOfficialSources={() => navigate(routes.courseStudentModule('official-sources'))}
        onOpenSuperAI={() => setIsAIOpen(true)}
        onOpenCreateRequest={() => setIsCreateRequestOpen(true)}
      />
      <ScientificGpsBlock openTo={routes.courseStudentModule('course-workspace')} />
    </CabinetWorkspaceFrame>
  );

  return (
    <div className="min-h-screen bg-[#040914] text-slate-100 p-4 sm:p-6 lg:p-8 role-cabinet-page">
      <Routes>
        <Route index element={cabinetHome} />
        <Route
          path="course-workspace"
          element={
            profile ? (
              <StudentCourseWorkspace
                profile={profile}
                disciplines={disciplines}
                selectedDisciplineCode={selectedDisciplineCode}
                onSelectDiscipline={handleSelectDiscipline}
                weeks={weeks}
                assignments={assignments}
                grades={grades}
                attendance={attendance}
                templates={templates}
                meetings={meetings}
                portfolioItems={portfolio.filter(p => p.discipline_code === selectedDisciplineCode)}
                requests={requests.filter(r => r.discipline_code === selectedDisciplineCode)}
                onBackToCabinet={backToCabinet}
                onOpenUploadModal={handleOpenUploadModal}
                onOpenTeacherRequestModal={() => setIsRequestModalOpen(true)}
                onOpenDocumentModal={(tpl) => {
                  setActiveTemplate(tpl);
                  setIsDocumentModalOpen(true);
                }}
                onTogglePortfolio={handleTogglePortfolio}
              />
            ) : (
              cabinetHome
            )
          }
        />
        <Route
          path="literature"
          element={
            <StudentLiteratureOverlay
              savedList={savedLiterature}
              onSearch={handleSearchLiterature}
              onSaveLiterature={handleSaveLiterature}
              onDeleteSaved={handleDeleteSavedLiterature}
              onBackToCabinet={backToCabinet}
            />
          }
        />
        <Route
          path="connections"
          element={
            <RoleAcademicConnectionsOverlay
              isOpen
              onClose={backToCabinet}
              roleContext="COURSE_STUDENT"
              roleTitle="Обучающийся курса"
            />
          }
        />
        <Route
          path="tools/*"
          element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
        />
        <Route path="meetings" element={<><button onClick={backToCabinet}>В кабинет</button><CourseMeetings code={selectedDisciplineCode} /></>} />
        <Route path="meetings/*" element={<><button onClick={backToCabinet}>В кабинет</button><CourseMeetings code={selectedDisciplineCode} /></>} />
        <Route
          path="scientific-projects"
          element={
            <StudentScientificProjectsOverlay
              projects={scientificProjects}
              onBackToCabinet={backToCabinet}
              onAddToPortfolio={handleTogglePortfolio}
            />
          }
        />
        <Route
          path="opportunities"
          element={
            <StudentOpportunitiesOverlay
              opportunities={opportunities}
              onToggleTracking={handleToggleOpportunity}
              onBackToCabinet={backToCabinet}
            />
          }
        />
        <Route
          path="official-sources"
          element={<StudentOfficialSourcesOverlay onBackToCabinet={backToCabinet} />}
        />
      </Routes>

      <StudentAIModal isOpen={isAIOpen} onClose={() => setIsAIOpen(false)} onAskAI={handleAskAI} />

      <StudentSubmissionModal key={activeAssignment?.id || 'closed'}
        isOpen={isSubmissionOpen}
        assignment={activeAssignment}
        onClose={() => {
          setIsSubmissionOpen(false);
          setActiveAssignment(null);
        }}
        onSubmit={handleSubmitAssignment}
      />

      <StudentRequestModal
        isOpen={isRequestModalOpen}
        disciplineCode={selectedDisciplineCode}
        teacherName={
          disciplines.find((d) => d.code === selectedDisciplineCode)?.teacher_name || undefined
        }
        onClose={() => setIsRequestModalOpen(false)}
        onSubmit={handleCreateRequest}
      />

      <StudentDocumentModal
        isOpen={isDocumentModalOpen}
        template={activeTemplate}
        onClose={() => {
          setIsDocumentModalOpen(false);
          setActiveTemplate(null);
        }}
      />

      <StudentChatModal
        isOpen={isChatModalOpen}
        groupCode={disciplines.find(d => d.code === selectedDisciplineCode)?.group_code || ''}
        currentUserName={profile?.full_name}
        messages={chatMessages}
        onClose={() => setIsChatModalOpen(false)}
        onSendMessage={handleSendChatMessage}
      />

      <CreateRequestEntry
        isOpen={isCreateRequestOpen}
        onClose={() => setIsCreateRequestOpen(false)}
        onBack={() => setIsCreateRequestOpen(false)}
        activeRole="COURSE_STUDENT"
        roleLabel="Обучающийся"
      />
    </div>
  );
};

export default StudentPage;
